import React, { useRef, useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Send, Loader2 } from "lucide-react";

const AVATAR = "https://images.unsplash.com/photo-1731532073013-ebc6caf15b7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxiZWFyZGVkJTIwY291bnRyeSUyMG1hbiUyMGNhcHxlbnwwfHx8fDE3Nzg0NzA5MjZ8MA&ixlib=rb-4.1.0&q=85";

const STARTERS = [
  "How do I get my truck unstuck from the mud?",
  "Best beer to bring to a tailgate?",
  "How do I impress my girlfriend's dad?",
  "What's the secret to good BBQ?",
];

export default function AskRedneck() {
  const [messages, setMessages] = useState([
    { role: "bot", text: "Well howdy, partner! I'm Cousin Cletus. Pull up a stump and ask me anything — life, love, trucks, BBQ, you name it. I reckon I got somethin' useful to say." }
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ask-redneck", { session_id: sessionId, question: q });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "bot", text: r.data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "Aw shoot, somethin' went sideways. Try askin' again in a sec." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20" data-testid="ask-page">
      <div className="mb-8 flex items-center gap-4">
        <div className="w-16 h-16 md:w-20 md:h-20 border-2 border-[#DD5422] overflow-hidden flex-shrink-0">
          <img src={AVATAR} alt="Cousin Cletus" className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-1">/ Ask A Redneck</div>
          <h1 className="font-anton text-4xl md:text-6xl uppercase tracking-tight">Cousin Cletus</h1>
          <div className="text-sm text-[#8D9688]">AI-Powered. Country Approved.</div>
        </div>
      </div>

      <div className="bg-[#151A14] border-2 border-[#2C332A] flex flex-col" style={{ height: "65vh", minHeight: 500 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="chat-scroll">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-3`}>
              {m.role === "bot" && (
                <div className="w-10 h-10 flex-shrink-0 border-2 border-[#2C332A] overflow-hidden">
                  <img src={AVATAR} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div data-testid={`msg-${m.role}-${i}`} className={`max-w-[80%] px-4 py-3 border-2 ${
                m.role === "user"
                  ? "bg-[#2C332A] border-[#2C332A] text-[#E5E5E5]"
                  : "bg-[#4B5320]/30 border-[#4B5320] text-[#E5E5E5]"
              }`}>
                <div className="text-xs uppercase tracking-[0.2em] text-[#DD5422] mb-1">
                  {m.role === "user" ? "You" : "Cousin Cletus"}
                </div>
                <div className="text-sm md:text-base whitespace-pre-wrap leading-relaxed">{m.text}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start gap-3" data-testid="chat-loading">
              <div className="w-10 h-10 flex-shrink-0 border-2 border-[#2C332A] overflow-hidden">
                <img src={AVATAR} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="bg-[#4B5320]/30 border-2 border-[#4B5320] px-4 py-3 flex items-center gap-2 text-[#8D9688]">
                <Loader2 className="w-4 h-4 animate-spin" /> Cletus is thinkin'...
              </div>
            </div>
          )}
        </div>

        <div className="border-t-2 border-[#2C332A] p-4">
          {messages.length <= 1 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {STARTERS.map((s, i) => (
                <button key={i} data-testid={`starter-${i}`} onClick={() => send(s)}
                  className="text-xs uppercase tracking-widest px-3 py-2 border-2 border-[#2C332A] hover:border-[#DD5422] text-[#8D9688] hover:text-[#E5E5E5]">
                  {s}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <input
              data-testid="ask-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Cousin Cletus anything..."
              className="flex-1 bg-[#0A0E0B] border-2 border-[#2C332A] focus:border-[#DD5422] outline-none px-4 py-3 text-[#E5E5E5] placeholder:text-[#8D9688]"
              disabled={loading}
            />
            <Button data-testid="ask-submit-btn" type="submit" disabled={loading || !input.trim()}
              className="bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest border-2 border-[#DD5422] h-auto px-6">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
